import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod'; // @ts-expect-error - TS2614 - Module '"src/api-parsers"' has no exported member 'Workflow'. Did you mean to use 'import Workflow from "src/api-parsers"' instead?
import type {Workflow} from 'src/api-parsers';
import type {RouteLocation, RouteLocationTo} from 'src/types/router';

import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'src/rerouter';

import {classify} from 'src/utils';
import * as urlFor from 'src/utils/url-for';
import {values} from 'src/utils/object';
import {getAccountPrivileges} from 'src/selectors/accounts';

import NewWorkflowIcon from 'src/images/chat-new-message.svg?noAttrs';

import TruncatedText from 'src/components/lib/truncated-text';
import {FormattedPlural} from 'src/components/lib/intl';

import SearchIcon from 'src/images/preview-icon.svg';
import ClearIcon from 'src/images/close-icon.svg';
import {useAudienceFilterSeparation} from 'src/hooks/product-flags';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import css from 'src/components/navigation/side.css';


type BaseProps = {
  workflows: {
    [id: string]: Workflow;
  };
  canEdit?: unknown;
  isAnalytics?: unknown;
};

type Props = BaseProps & {
  resolveLocation: (workflowId: string) => RouteLocationTo;
};

const WorkflowDetail = ({
  workflows,
  resolveLocation,
  canEdit,
  isAnalytics,
}: Props) => {
  const {t} = useLabelTranslation();
  const [queryTerm, setQueryTerm] = useState('');
  const privileges = useSelector(getAccountPrivileges);
  const isJourneyV2 = useAudienceFilterSeparation();
  const journeyPageRoute = isJourneyV2
    ? '/journeys/goal-selection'
    : '/journey/new';
  // @ts-expect-error - TS18049 - 'privileges' is possibly 'null' or 'undefined'.
  const canCreateWorkflow = privileges.includes('EDIT_WORKFLOWS');

  const eventWorkflows = values(workflows).filter(({dbCleanup}) => !dbCleanup);

  const filteredWorkflows = eventWorkflows
    .sort((a, b) => a.name.localeCompare(b.name, 'en', {sensitivity: 'base'}))
    .filter((workflow: Workflow) =>
      workflow.name.toLowerCase().includes(queryTerm.toLowerCase()),
    );

  const hasSearchTerm = filteredWorkflows.length !== eventWorkflows.length;

  return (
    <div className={css.detail}>
      <section className={css.detailSection}>
        <div className={css.detailTitleWorkflows}>
          <div>Journeys</div>
          {!isAnalytics && canCreateWorkflow && (
            <Link className={css.detailNewWorkflowButton} to={journeyPageRoute}>
              {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
              <NewWorkflowIcon className={css.newWorkflowIcon} />
            </Link>
          )}
        </div>
        <label className={css.workflowSearchLabel}>
          <SearchIcon className={css.searchIcon} />
          <input
            className={css.workflowSearch}
            value={queryTerm}
            onChange={(event) => setQueryTerm(event.currentTarget.value)}
            placeholder={t('Search', 'Search') + '...'}
          />
          {hasSearchTerm && (
            <ClearIcon
              className={css.clearIcon}
              onClick={() => setQueryTerm('')}
            />
          )}
        </label>
        {hasSearchTerm && (
          <div
            className={classify(
              css.detailTitleWorkflows,
              css.workflowSearchResults,
            )}
          >
            <span>
              {filteredWorkflows.length}{' '}
              <FormattedPlural
                value={filteredWorkflows.length}
                one={'result'}
                other={'Results'}
              />{' '}
              found:
            </span>
          </div>
        )}

        <div className={css.detailWorkflows}>
          {filteredWorkflows.map((workflow: Workflow) => (
            <TruncatedText
              text={workflow.name}
              limit={20}
              component={Link}
              className={classify(css.detailWorkflow, {
                [css.active]: workflow.active,
              })}
              activeClassName={css.on}
              to={resolveLocation(workflow.id)}
              key={workflow.id}
            />
          ))}
          {Boolean(canEdit) && !hasSearchTerm && (
            <Link className={css.detailWorkflowNew} to={journeyPageRoute}>
              Add New Journey
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export const AnalyticsWorkflowList: Flow_AbstractComponent<BaseProps, unknown> =
  React.memo<BaseProps>((props) => (
    <WorkflowDetail
      {...props}
      resolveLocation={(workflowId) => (location: RouteLocation) => ({
        ...location,
        pathname: urlFor.analyticsWorkflow(workflowId),
      })}
    />
  ));

export const DefaultWorkflowList: Flow_AbstractComponent<BaseProps, unknown> =
  React.memo<BaseProps>((props) => (
    <WorkflowDetail
      {...props}
      resolveLocation={(workflowId) => urlFor.workflow(workflowId)}
    />
  ));
